import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AddressStep from "./steps/AddressStep";
import ConfirmMappingStep from "./steps/ConfirmMappingStep";
import ContractStep from "./steps/ContractStep";
import MatchingStep from "./steps/MatchingStep";
import PhoneStep from "./steps/PhoneStep";
import RecommendStep from "./steps/RecommendStep";
import SuccessStep from "./steps/SuccessStep";
import WaitlistStep from "./steps/WaitlistStep";
import type { AddressData, FlowStep, Supplier, WaitlistReason } from "./types";

/**
 * 新用戶自動配對註冊流程控制器（單頁狀態機）。
 *
 * 手機 OTP → 依手機配對既有客戶
 *   ├─ 命中 → 確認瓦斯行 → 線上簽約 → 完成
 *   └─ 未命中 → 填地址 → 依地址配對
 *        ├─ 有推薦 → 推薦池 →（選定 → 簽約 → 完成 / 都不喜歡 → 候補）
 *        └─ 無合作店 → 候補
 *
 * 配對／推薦／簽約皆為 mock（見 mockData.ts），待後端 API 就緒後替換。
 */
const NewUserRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<FlowStep>("phone");
  const [phone, setPhone] = useState("");
  // phoneVerifiedTicket：手機驗證票證，後續接簽約/註冊 API 會用到。
  const [, setPhoneVerifiedTicket] = useState("");
  const [address, setAddress] = useState<AddressData | null>(null);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [waitlistReason, setWaitlistReason] = useState<WaitlistReason>("no_supplier_in_area");

  // 手機配對命中的既有客戶 → 走「確認」分支（有 confidence）。
  const isMatchedExisting = !!supplier?.confidence;

  const goHome = () => navigate("/home", { replace: true });

  switch (step) {
    case "phone":
      return (
        <PhoneStep
          onResult={(p, ticket, matched) => {
            setPhone(p);
            setPhoneVerifiedTicket(ticket);
            if (matched) {
              // 命中既有瓦斯行 → 直接確認（無過場畫面）
              setSupplier(matched);
              setStep("confirm");
            } else {
              // 未命中 → 直接填配送地址
              setStep("address");
            }
          }}
          onBack={() => navigate(-1)}
        />
      );

    case "address":
      return (
        <AddressStep
          onNext={(a) => {
            setAddress(a);
            setStep("matchingAddress");
          }}
          onBack={() => setStep("phone")}
        />
      );

    case "matchingAddress":
      return (
        <MatchingStep
          phone={phone}
          onRecommend={() => setStep("recommend")}
          onWaitlist={() => {
            setWaitlistReason("no_supplier_in_area");
            setStep("waitlist");
          }}
        />
      );

    case "confirm":
      return (
        <ConfirmMappingStep
          supplier={supplier!}
          address={address}
          onConfirm={(s) => {
            setSupplier(s);
            setStep("contract");
          }}
          onReject={() => {
            setSupplier(null);
            setStep("address");
          }}
          onBack={() => setStep("phone")}
        />
      );

    case "recommend":
      return (
        <RecommendStep
          onSelect={(s) => {
            setSupplier(s);
            setStep("contract");
          }}
          onAllRejected={() => {
            setWaitlistReason("user_rejected_recommendations");
            setStep("waitlist");
          }}
          onBack={() => setStep("address")}
        />
      );

    case "contract":
      return (
        <ContractStep
          supplier={supplier!}
          isExisting={isMatchedExisting}
          onSigned={() => setStep("success")}
          onBack={() => setStep(isMatchedExisting ? "confirm" : "recommend")}
        />
      );

    case "waitlist":
      return <WaitlistStep reason={waitlistReason} address={address} onDone={goHome} />;

    case "success":
      return <SuccessStep supplier={supplier} onDone={goHome} />;

    default:
      return null;
  }
};

export default NewUserRegister;
